import { DateTime } from "luxon";
import { z } from "zod";

import { normalizeSimpleDate } from "@/src/lib/utils/date-and-phone-utils";

export const ecwInt = z.string().transform((value, ctx) => {
  if (value === "") return null;

  const parsed = Number(value);

  if (!Number.isInteger(parsed)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `ecwInt: expected integer string, got ${JSON.stringify(value)}`,
    });

    return z.NEVER;
  }

  return parsed;
});

export const ecwNum = z.string().transform((value, ctx) => {
  if (value === "") return null;

  const parsed = Number(value);

  if (!Number.isFinite(parsed)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `ecwNum: expected numeric string, got ${JSON.stringify(value)}`,
    });

    return z.NEVER;
  }

  return parsed;
});

export const ecwBool = z.string().transform((value, ctx) => {
  if (value === "") return null;
  if (value === "1" || value === "true") return true;
  if (value === "0" || value === "false") return false;

  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    message: `ecwBool: expected 0/1/true/false, got ${JSON.stringify(value)}`,
  });

  return z.NEVER;
});

export const ecwDate = z.string().transform((value, ctx) => {
  if (value === "") return null;

  const normalized = normalizeSimpleDate(value);

  if (normalized === null) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: `ecwDate: unrecognized date ${JSON.stringify(value)}` });

    return z.NEVER;
  }

  return normalized;
});

const ECW_DATE_FORMATS = ["yyyy-MM-dd", "M/d/yyyy"];

const ECW_DATETIME_FORMATS = [
  "yyyy-MM-dd'T'H:mm:ss",
  "yyyy-MM-dd'T'H:mm",
  "yyyy-MM-dd H:mm:ss",
  "yyyy-MM-dd H:mm",
  "M/d/yyyy h:mm:ss a",
  "M/d/yyyy h:mm a",
  "M/d/yyyy H:mm:ss",
  "M/d/yyyy H:mm",
  "MMM d,yyyy h:mm:ss a",
  "MMM d,yyyy h:mm a",
];

function normalizeEcwDateTime(value: string): string | null {
  for (const format of ECW_DATE_FORMATS) {
    const dt = DateTime.fromFormat(value, format);

    if (dt.isValid) return dt.toFormat("yyyy-MM-dd");
  }

  for (const format of ECW_DATETIME_FORMATS) {
    const dt = DateTime.fromFormat(value, format);

    if (dt.isValid) return dt.toFormat("yyyy-MM-dd'T'HH:mm:ss");
  }

  return null;
}

export const ecwDateTime = z.string().transform((value, ctx) => {
  if (value === "") return null;

  const normalized = normalizeEcwDateTime(value);

  if (normalized === null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `ecwDateTime: unrecognized date/datetime ${JSON.stringify(value)}`,
    });

    return z.NEVER;
  }

  return normalized;
});

export function ecwEnum<const T extends readonly [string, ...string[]]>(values: T) {
  const inner = z.enum(values);

  return z.string().transform((value, ctx) => {
    if (value === "") return null;

    const result = inner.safeParse(value);

    if (!result.success) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `ecwEnum: unexpected value ${JSON.stringify(value)} (known: ${values.join(", ")})`,
      });

      return z.NEVER;
    }

    return result.data;
  });
}

export function ecwList<T extends z.ZodTypeAny>(inner: T, key: string) {
  return z.preprocess((value) => {
    if (value === "" || value == null) return [];

    if (Array.isArray(value)) return value;

    if (typeof value === "object") {
      const raw = (value as Record<string, unknown>)[key];

      if (raw === "" || raw == null) return [];

      return Array.isArray(raw) ? raw : [raw];
    }

    return value;
  }, z.array(inner));
}

export interface EcwAssignee {
  id: string;
  name: string;
}

export function buildAssignee(id: string | undefined, name: string | undefined): EcwAssignee | undefined {
  if (id === undefined && name === undefined) return undefined;

  if (id === undefined || name === undefined) {
    throw new Error("assignee id and name must both be provided or both be omitted");
  }

  return { id, name };
}
